import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'
import { Base } from '@itsrever/design-system'

const {
  TabsRoot,
  TabsList,
  Tab,
  TabPanel,
  Table,
  TabsIndicator,
  Badge,
  EmptyState,
  Spinner
} = Base

const TAB_PARAM = 'return_status'
const PAGE_PARAM = 'page'

export interface ListTab<T> {
  value: string
  label: string
  // undefined until the first page arrives, so the table shows its skeleton
  rows: T[] | undefined
  count: number
  isPending: boolean
  isFetching: boolean
  hidden?: boolean
}

interface ListTabsProps<T> {
  tabs: ListTab<T>[]
  allTab: string
  pageSize: number
  columns: Parameters<typeof Table<T>>[0]['columns']
  onRowClick: (row: T) => void
  pinFirstColumn?: boolean
}

// Tab and page live in the URL; every tab but the open one stays on its first page.
export const useListTabsPage = (allTab: string, pageSize: number) => {
  const [searchParams] = useSearchParams()
  const currentTab = searchParams.get(TAB_PARAM) ?? allTab
  const pageIndex = Number(searchParams.get(PAGE_PARAM) ?? 0)

  return {
    currentTab,
    pageIndex,
    offsetFor: (tab: string) => (tab === currentTab ? pageIndex * pageSize : 0)
  }
}

export const ListTabs = <T,>({
  tabs,
  allTab,
  pageSize,
  columns,
  onRowClick,
  pinFirstColumn
}: ListTabsProps<T>) => {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const { currentTab, pageIndex } = useListTabsPage(allTab, pageSize)

  const selectTab = (value: string) => {
    searchParams.set(TAB_PARAM, value)
    searchParams.delete(PAGE_PARAM)
    setSearchParams(searchParams)
  }

  const changePage = (index: number) => {
    if (index === 0) {
      searchParams.delete(PAGE_PARAM)
    } else {
      searchParams.set(PAGE_PARAM, index.toString())
    }
    setSearchParams(searchParams)
  }

  return (
    <div className="relative rounded-lg border border-solid border-neutral-200 bg-white pt-2.5 shadow-sm [overflow:clip]">
      {tabs.some((tab) => tab.isFetching) && (
        <Spinner
          label={t('list_tabs.loading')}
          size="lg"
          className="absolute right-4"
        />
      )}
      <TabsRoot onValueChange={selectTab} value={currentTab}>
        <TabsList className="pl-2 shadow-none">
          <TabsIndicator />
          {tabs
            .filter((tab) => !tab.hidden)
            .map((tab) => (
              <Tab key={tab.value} value={tab.value}>
                {tab.label}
                <Badge variant="count" className="min-w-7">
                  {tab.count.toLocaleString()}
                </Badge>
              </Tab>
            ))}
        </TabsList>
        {tabs.map((tab) => (
          <TabPanel key={tab.value} value={tab.value}>
            {tab.rows?.length === 0 ? (
              <EmptyState
                title={t('list_tabs.no_results')}
                description={t('list_tabs.no_results_description')}
                className="h-64"
              />
            ) : (
              <Table
                data={tab.rows ?? []}
                columns={columns}
                rowProps={{ onClick: onRowClick }}
                loading={tab.isPending}
                pinFirstColumn={pinFirstColumn}
                pagination={{
                  pageCount: Math.ceil(tab.count / pageSize),
                  pageIndex,
                  pageSize,
                  onPageChange: changePage
                }}
              />
            )}
          </TabPanel>
        ))}
      </TabsRoot>
    </div>
  )
}
